<template>
  <q-page class="q-pa-xl">
    <div class="row fit align-center items-center q-mb-md">
      <span
        id="dashboard_title"
        class="text-h4"
      >{{ $page.props.i18n.title }}</span>
      <span class="text-h5 text-muted q-px-md">|</span>

      <!-- Dashboard Selector -->
      <q-select
        v-model="state_dashboards_selected"
        :options="dashboards"
        emit-value
        map-options
        option-value="link"
        option-label="label"
        class="dashboard-view-selector text-h4"
        style="width: fit-content"
        dense
        borderless
        flat
        :hide-dropdown-icon="dashboards.length === 1"
      />
    </div>

    <!-- Filters -->
    <div class="row justify-start items-start content-start align-start q-pt-md">
      <!-- ExchangeAccount (disabled) # TODO: remove disabled filter because, why keep it? -->
      <q-select
        :model-value="null"
        outlined
        disabled
        dense
        options-dense
        hide-bottom-space
        :options="[]"
        clearable
        color="primary"
        style="max-width: fit-content; width: 100%"
        :class="{
          'select-filter': true,
          'select-filter-active': false,
          'q-mr-sm': true,
          'q-mb-sm': true,
        }"
      >
        <template v-slot:selected>
          <div>
            {{ i18n.exchange_account_filter_all }}
          </div>
        </template>
      </q-select>

      <!-- Asset Selector with "Total" option and multiple selections -->
      <q-select
        v-model="filters.assets_filter_values"
        outlined
        dense
        options-dense
        hide-bottom-space
        :options="assets"
        :prefix="`${i18n.assets_filter_label}${assets_filter_values.length ? ':' : ''}`"
        option-label="symbol"
        option-value="id"
        color="primary"
        style="max-width: fit-content; width: 100%"
        :class="{
          'select-filter': true,
          'select-filter-active': assets_filter_values.length,
          'q-mr-sm': true,
          'q-mb-sm': true
        }"
        input-class=""
        popup-content-class="q-px-md q-pb-none q-pt-md"
        map-options
        clearable
        multiple
        :max-values="3"
        @update:model-value="assets_filter_update"
      >
        <template v-slot:option="scope">
          <q-item
            v-bind="scope.itemProps"
            :class="{ 'text-accent text-bold': assets_filter_values.find(id => id == scope.opt.id) }"
          >
            <q-item-section
              avatar
              class="q-mr-none q-pr-none"
            >
              <q-avatar
                v-if="scope.opt"
                size="18px"
                square
              >
                <img
                  :src="scope.opt.logo_url"
                  :alt="scope.opt"
                >
              </q-avatar>
            </q-item-section>
            <q-item-section>
              <q-item-label class="max-w-[32ch] ellipsis">
                {{ scope.opt.name }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              side
              class="q-ml-lg"
            >
              <q-item-label class="text-bold max-w-[8ch] ellipsis">
                {{ scope.opt.symbol }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>


        <!-- "Total" Option -->
        <template v-slot:before-options>
          <q-item class="">
            <q-item-section>
              <q-item-label class="text-bold">
                {{ i18n.assets_filter_total_display_title }}
              </q-item-label>
              <q-item-label class="text-caption text-muted">
                {{ i18n.assets_filter_total_display_label }}
              </q-item-label>
            </q-item-section>

            <q-item-section side>
              <q-toggle
                v-model="filters.assets_filter_total_display"
                color="accent"
                size="xs"
                @update:model-value="filters_update"
              />
            </q-item-section>
          </q-item>
          <q-separator class="q-mb-md" />
        </template>

        <!-- n maximum values -->
        <template v-slot:after-options>
          <q-separator class="q-mt-md" />
          <q-item class="">
            <q-item-section>
              <q-item-label class="text-caption text-muted text-italic">
                {{ i18n.assets_filter_max_text }}
              </q-item-label>
            </q-item-section>

            <!-- Show 'x/3' on side  -->
            <q-item-section side>
              <q-item-label class="text-muted">
                {{ `${assets_filter_values.length}/3` }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>

        <!-- No data available -->
        <template v-slot:no-option>
          <q-item dense>
            <q-item-section>
              <q-item-label>{{ i18n.assets_filter_empty }}</q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>

      <!-- PNL Types Display Filters -->
      <div>
        <q-radio
          v-model="filters.pnl_filter"
          val="all"
          :label="i18n.pnl_filter_all"
          @update:model-value="filters_update"
        />
        <q-radio
          v-model="filters.pnl_filter"
          val="realized"
          :label="i18n.pnl_filter_realized"
          @update:model-value="filters_update"
        />
        <q-radio
          v-model="filters.pnl_filter"
          val="latent"
          :label="i18n.pnl_filter_latent"
          class="q-mr-md"
          @update:model-value="filters_update"
        />
      </div>

      <q-space />

      <!-- Date -->
      <DatePicker
        :min-date="graphs_dates_min"
        :max-date="graphs_dates_max"
        :selected-date="date_picker_formatted_date"
        :tooltip-next="i18n.day_next"
        :tooltip-previous="i18n.day_previous"
        @update:selected-date="on_date_picker_click($event)"
      />
    </div>

    <!-- Graphs -->
    <div class="row full_width q-col-gutter-lg q-pt-md">
      <div class="col-12">
        <!-- PNL History Graph -->
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-pt-sm q-pb-none">
            <div class="col-shrink align-center justify-center item-center q-card-title q-py-sm gt-xs row items-center no-wrap">
              <span>{{ graph_pnl_histories_title }}</span>
              <span class="text-muted text-caption q-ml-sm">
                &middot; {{ date_format(day) }}
              </span>
            </div>
            <div class="col text-right">
              <q-btn-toggle
                v-model="graph_pnl_timerange"
                unelevated
                class="date-range-btn"
                size="sm"
                toggle-color="primary"
                :options="graphs_timeranges"
              />
            </div>
          </q-card-section>
          <q-card-section
            v-if="graph_pnl_histories?.length > 0"
            class="q-pb-md q-pt-none"
          >
            <Line
              ref="graph_pnl_ref"
              :data="graph_pnl_chart_data"
              :options="graph_pnl_chart_options"
              :height="270"
            />
          </q-card-section>
          <q-card-section
            v-else
            class="self-stretch q-pt-md"
            style="height: 270px"
          >
            <div class="text-center row items-center justify-center q-pa-none fit full-height text-light self-stretch">
              <div class="col-12">
                <!-- No Data / Ghost -->
                <iframe
                  type="image/svg+xml"
                  src="/animations/ghost_animation.svg"
                  class="inline"
                  style="height: 140px"
                  sandbox="allow-scripts"
                />
                <p
                  class="text-default"
                >
                  {{ i18n.no_history_available }}
                </p>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-md-12 col-lg-6">
        <!-- WAC History Graph -->
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-py-sm">
            <div class="col-shrink align-center justify-center item-center q-card-title q-py-sm gt-xs row items-center no-wrap">
              <span>{{ graph_wac_histories_title }}</span>
              <span class="text-muted text-caption q-ml-sm">
                &middot; {{ date_format(day) }}
              </span>
            </div>
            <div class="col text-right">
              <q-btn-toggle
                v-model="graph_wac_timerange"
                unelevated
                class="date-range-btn"
                size="sm"
                toggle-color="primary"
                :options="graphs_timeranges"
              />
            </div>
          </q-card-section>
          <q-card-section
            v-if="graph_wac_histories"
            class="q-pb-md q-pt-none"
          >
            <Line
              ref="graph_wac_ref"
              :data="graph_wac_chart_data"
              :options="graph_wac_chart_options"
              :height="270"
            />
          </q-card-section>
          <q-card-section
            v-else
            class="self-stretch q-pt-md"
            style="height: 270px"
          >
            <div class="text-center row items-center justify-center q-pa-none fit full-height text-light self-stretch">
              <div class="col-12">
                <!-- No Data / Ghost -->
                <iframe
                  type="image/svg+xml"
                  src="/animations/ghost_animation.svg"
                  class="inline"
                  style="height: 140px"
                  sandbox="allow-scripts"
                />
                <p
                  class="text-default"
                >
                  {{ i18n.no_history_available }}
                </p>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-md-12 col-lg-6">
        <!-- Balance History Graph -->
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-py-sm">
            <div class="col-shrink align-center justify-center item-center q-card-title q-py-sm gt-xs row items-center no-wrap">
              <span>{{ graph_balance_histories_title }}</span>
              <span class="text-muted text-caption q-ml-sm">
                &middot; {{ date_format(day) }}
              </span>
            </div>
            <div class="col text-right">
              <q-btn-toggle
                v-model="graph_balance_timerange"
                unelevated
                class="date-range-btn"
                size="sm"
                toggle-color="primary"
                :options="graphs_timeranges"
              />
            </div>
          </q-card-section>
          <q-card-section
            v-if="graph_wac_histories"
            class="q-pb-md q-pt-none"
          >
            <Line
              ref="graph_balance_ref"
              :data="graph_balance_chart_data"
              :options="graph_balance_chart_options"
              :height="270"
            />
          </q-card-section>
          <q-card-section
            v-else
            class="self-stretch q-pt-md"
            style="height: 270px"
          >
            <div class="text-center row items-center justify-center q-pa-none fit full-height text-light self-stretch">
              <div class="col-12">
                <!-- No Data / Ghost -->
                <iframe
                  type="image/svg+xml"
                  src="/animations/ghost_animation.svg"
                  class="inline"
                  style="height: 140px"
                  sandbox="allow-scripts"
                />
                <p
                  class="text-default"
                >
                  {{ i18n.no_history_available }}
                </p>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <!-- PNL Data of the Day -->
    <div class="row full_width q-col-gutter-lg q-mt-sm">
      <div class="col-12 self-stretch">
        <q-card class="fit">
          <q-card-section class="row items-center justify-space-between q-pb-none">
            <div class="col">
              <span class="q-card-title">{{ i18n.assets_table_title }} <span class="text-muted">({{ date_format(day) }})</span> </span>
            </div>
          </q-card-section>

          <q-card-section
            class="row items-start justify-space-between q-pt-md q-px-none"
          >
            <q-table
              id="assets-table"
              v-model:pagination="assets_table_pagination"
              :rows="assets_table"
              :columns="assets_table_columns"
              row-key="id"
              class="full-width"
              flat
              dense
              :no-data-label="$q.lang.table.noResults"
              separator="none"
              hide-pagination
              :rows-per-page-options="[0]"
            >
              <template v-slot:top-row>
                <q-tr style="height: 48px;">
                  <q-td align="left">
                    <span class="text-bold">{{ i18n.assets_table_toprow_title }}</span>
                  </q-td>
                  <q-td align="right">
                    <span class="text-muted">---</span>
                  </q-td>
                  <q-td align="right">
                    <span class="text-muted">---</span>
                  </q-td>
                  <q-td align="right">
                    <span class="text-bold">{{ assets_table_total.pnl_latent_total }}</span>
                  </q-td>
                  <q-td align="right">
                    <span class="text-bold">{{ assets_table_total.pnl_realized_total }}</span>
                  </q-td>
                </q-tr>
              </template>

              <template v-slot:body-cell-asset_symbol="props">
                <q-td
                  :props="props"
                  class=" "
                >
                  <div class="col-shrink">
                    <!-- Asset Icon -->
                    <img
                      width="18px"
                      height="18px"
                      class="inline q-mr-xs"
                      :alt="props.value"
                      :src="props.row.asset_icon"
                    >

                    {{ props.row.asset_symbol_truncated }}
                    <q-tooltip
                      v-if="props.value != props.row.asset_symbol_truncated"
                      :delay="500"
                      anchor="bottom middle"
                      self="top middle"
                    >
                      {{ props.value }}
                    </q-tooltip>
                  </div>
                </q-td>
              </template>
            </q-table>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-page>
</template>
<script>
import DatePicker from '@/pages/w/dashboard/date_picker.vue';
import Layout from '@/layouts/workspace.vue';
import { usePoll } from '@inertiajs/vue3';

import { CategoryScale } from "chart.js";
import { Chart as ChartJS }  from "chart.js";
import { Filler } from "chart.js";
import { Interaction } from "chart.js";
import { Line } from "vue-chartjs";
import { LineElement } from "chart.js";
import { LinearScale } from "chart.js";
import { PointElement } from "chart.js";
import { TimeScale } from "chart.js";
import { Tooltip } from "chart.js";
import { CrosshairPlugin } from "chartjs-plugin-crosshair";
import { Interpolate } from "chartjs-plugin-crosshair";
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";
import { date as qdate } from "quasar";

const CustomCrosshairPlugin = function (plugin) {
  const originalAfterDraw = plugin.afterDraw;
  plugin.afterDraw = function (chart, easing) {
    if (chart && chart.crosshair) {
      originalAfterDraw.call(this, chart, easing);
    }
  };
  return plugin;
};

ChartJS.register(
  CategoryScale,
  LineElement,
  Tooltip,
  LinearScale,
  PointElement,
  TimeScale,
  Filler,
  annotationPlugin,
  zoomPlugin,
  CustomCrosshairPlugin(CrosshairPlugin)
);

Interaction.modes.interpolate = Interpolate;

import { ArcElement } from "chart.js";
import { Legend } from "chart.js";
import { getChartLabelPlugin } from "chart.js-plugin-labels-dv";
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  getChartLabelPlugin()
);

export default {
  components: {
    DatePicker,
    Line,
  },
  layout: Layout,
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    dashboards: {
      type: Array,
      required: true,
    },
    dashboards_selected: {
      type: String,
      required: true,
    },
    filter_action: {
      type: String,
      requireq: true,
    },
    day: {
      type: String,
      required: true,
    },
    fiat: {
      type: String,
      required: true,
    },
    wac: {
      type: Object,
      required: true,
    },
    graphs_dates_min: {
      type: String,
      required: true,
    },
    graphs_dates_max: {
      type: String,
      required: true,
    },
    graph_pnl_histories_title: {
      type: String,
      required: true,
    },
    graph_pnl_histories: {
      type: Object,
      required: true,
    },
    graph_wac_histories_title: {
      type: String,
      required: true,
    },
    graph_wac_histories: {
      type: Object,
      required: true,
    },
    graph_balance_histories_title: {
      type: String,
      required: true,
    },
    graph_balance_histories: {
      type: Object,
      required: true,
    },
    assets : {
      type: Array,
      required: true,
    },
    assets_filter_values: {
      type: Array,
      required: true,
    },
    assets_filter_total_display: {
      type: Boolean,
      required: true,
    },
    pnl_filter: {
      type: String,
      required: true,
    },
    assets_table: {
      type: Array,
      required: true,
    },
    assets_table_total: {
      type: Object,
      required: true,
    },
  },
  data() {
    const poll = usePoll(
      1000,
      {
        only: ["wac"],
        data: { ajax_bar_skip: '' },
      },
      { autoStart: this.wac.polling }
    );

    return {
      state_dashboards_selected: this.dashboards_selected,
      wac_polling_start: poll.start,
      wac_polling_stop: poll.stop,
      filters: {
        day: this.day,
        pnl_filter: this.pnl_filter,
        assets_filter_total_display: this.assets_filter_total_display,
        assets_filter_values: this.assets_filter_values,
      },
      graph_pnl_timerange: 'all',
      graph_wac_timerange: 'all',
      graph_balance_timerange: 'all',
      date_selected: this.day,
      graph_pnl_chart_options: this.chart_options_default(this.fiat),
      graph_wac_chart_options: this.chart_options_default(this.fiat),
      graph_balance_chart_options: this.chart_options_default(),
      assets_table_pagination: {
        rowsPerPage: -1,
        sortBy: "pnl_realized",
        descending: true,
      },
      assets_table_columns: [
        {
          name: "asset_symbol",
          align: "left",
          label: this.$page.props.i18n.assets_table_column_1,
          field: (row) => row.asset_symbol,
          sortable: true,
        },
        {
          name: "wac",
          align: "right",
          label: this.$page.props.i18n.assets_table_column_2,
          field: (row) => row.wac_formatted,
          sort: (a, b, row_a, row_b) => row_a.wac - row_b.wac,
          sortable: true,
        },
        {
          name: "balance",
          align: "right",
          label: this.$page.props.i18n.assets_table_column_3,
          field: (row) => row.balance_formatted,
          sort: (a, b, row_a, row_b) => row_a.balance - row_b.balance,
          sortable: true,
        },
        {
          name: "pnl_latent",
          align: "right",
          label: this.$page.props.i18n.assets_table_column_4,
          field: (row) => row.pnl_latent_formatted,
          sort: (a, b, row_a, row_b) => row_a.pnl_latent - row_b.pnl_latent,
          sortable: true,
        },
        {
          name: "pnl_realized",
          align: "right",
          label: this.$page.props.i18n.assets_table_column_5,
          field: (row) => row.pnl_realized_formatted,
          sort: (a, b, row_a, row_b) => row_a.pnl_realized - row_b.pnl_realized,
          sortable: true,
        },
      ],
    };
  },
  computed: {
    date_picker_formatted_date() {
      return qdate.formatDate(this.date_selected, "YYYY/MM/DD");
    },
    graphs_timeranges() {
      return [
        { label: this.i18n.time_range_1_week, value: '1w' },
        { label: this.i18n.time_range_1_month, value: '1m' },
        { label: this.i18n.time_range_3_months, value: '3m' },
        { label: this.i18n.time_range_ytd, value: 'ytd' },
        { label: this.i18n.time_range_all, value: 'all' }
      ];
    },
    graph_pnl_chart_data() {
      return {
        datasets: this.graph_pnl_histories.map((dataset) => {
          return this.chart_dataset_default(dataset);
        }),
      };
    },
    graph_wac_chart_data() {
      return {
        datasets: this.graph_wac_histories.map((dataset) => {
          return this.chart_dataset_default(dataset);
        }),
      };
    },
    graph_balance_chart_data() {
      return {
        datasets: this.graph_balance_histories.map((dataset) => {
          return this.chart_dataset_default(dataset);
        }),
      };
    },
  },
  watch: {
    'wac.polling': {
      handler: function(new_value, old_value) {
        if (new_value) {
          this.wac_polling_start();
        } else {
          if (old_value) {
            this.$inertia.get('');
          }
          this.wac_polling_stop();
        }
      },
      immediate: true,
    },
    state_dashboards_selected(new_value) {
      this.$inertia.get(new_value);
    },
    graph_pnl_timerange(new_value) {
      this.graph_timerange_change(
        new_value,
        this.$refs.graph_pnl_ref,
        this.graphs_dates_min,
        this.graphs_dates_max,
        this.date_selected,
      );
    },
    graph_wac_timerange(new_value) {
      this.graph_timerange_change(
        new_value,
        this.$refs.graph_wac_ref,
        this.graphs_dates_min,
        this.graphs_dates_max,
        this.date_selected,
      );
    },
    graph_balance_timerange(new_value) {
      this.graph_timerange_change(
        new_value,
        this.$refs.graph_balance_ref,
        this.graphs_dates_min,
        this.graphs_dates_max,
        this.date_selected,
      );
    },
  },
  methods: {
    wac_update: function() {
      this.$inertia.post(
        this.wac.update_action,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
            this.form_errors = errors;
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    chart_dataset_default: function(dataset) {
      return {
        label: dataset.label,
        data: dataset.data,
        borderColor: dataset.border_color,
        borderDash: dataset.border_dash,
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 6,
        tension: 0.4,
        fill: false,
      };
    },
    chart_options_default: function(fiat) {
      let minDate = Infinity;
      let maxDate = Infinity;
      if (this.graphs_dates_min.length > 0) {
        minDate = new Date(this.graphs_dates_min).getTime();
      }
      if (this.graphs_dates_max.length > 0) {
        maxDate = new Date(this.graphs_dates_max).getTime();
      }

      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          y: {
            beginAtZero: true,
          },
          x: {
            min: minDate,
            max: maxDate,
            type: "time",
            time: {
              unit: "day",
              parser: "yyyy-MM-dd",
              displayFormats: {
                day: "dd/MM/yy", // Adjusted format
              },
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 10, // Adjust as needed to manage label density
            },
            grid: {
              display: false, // This hides the vertical grid lines
            },
          },
        },
        plugins: {
          legend: {
            display: true,
            labels: {
              padding: 20,
              boxWidth: 16,
              boxHeight: 16,
            },
            position: "bottom",
            onClick: (event) => event.stopPropagation(),
          },
          annotation: {
            annotations: {
              // Dashed line that indicate the selected date
              currentDateAnnotation: this.date_annotation,
            },
          },
          tooltip: {
            enabled: true,
            mode: "index",
            backgroundColor: "#ffffff", // White background
            titleColor: "#000000", // Black title
            bodyColor: "#000000", // Black body
            padding: 12,
            caretSize: 0,
            caretPadding: 64,
            borderColor: "#eee",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            intersect: false,
            usePointStyle: true,
            callbacks: {
              labelPointStyle: function() {
                return {
                  pointStyle: 'circle',
                  rotation: 0,
                };
              },
              title: function (tooltipItems) {
                return qdate.formatDate(tooltipItems[0].parsed.x, "DD/MM/YYYY");
              },
              label: (tooltipItem) => {
                const datasetLabel = tooltipItem.dataset.label || '';
                if (fiat) {
                  return `${datasetLabel}: ${this.currency_format(parseFloat(tooltipItem.raw[1]), fiat)}`;
                } else {
                  return `${datasetLabel}: ${parseFloat(tooltipItem.raw[1])}`;
                }
              },
            }
          },
          zoom: {
            zoom: {
              wheel: {
                enabled: true,
              },
              pinch: {
                enabled: false,
              },
              mode: "x",
              scaleMode: "x",
            },
            pan: {
              enabled: true,
              mode: "x",
            },
            limits: {
              x: {
                // min range is a 5 days in timestamp.
                minRange: 5 * 24 * 60 * 60 * 1000,
                min: minDate,
                max: maxDate,
              },
            },
          },
          crosshair: {
            line: {
              color: "#ddd", // crosshair line color
              width: 1, // crosshair line width
            },
            sync: {
              enabled: false, // enable trace line syncing with other charts
            },
            zoom: {
              enabled: false, // enable zooming
            },
          },
        },
        onClick: this.on_chart_click,
        interaction: {
          mode: "nearest",
          intersect: false,
          axis: "x",
        },
      };
    },
    filters_update: function() {
      this.$inertia.get(
        this.filter_action,
        this.filters,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'day',
            'pnl_filter',
            'assets_filter_values',
            'assets_filter_total_display',
            'graph_pnl_histories',
            'graph_wac_histories',
            'graph_balance_histories',
          ],
        },
      );
    },
    assets_filter_update: function(ids_and_asset) {
      let ids = [];

      if (ids_and_asset != void 0) {
        ids = ids_and_asset.map((a) => {
          if (a?.id) {
            return a.id; // New value is an object from the filter options.
          } else {
            return a; // Old values are strings from the filter values.
          }
        });
      }

      this.filters.assets_filter_values = ids;
      this.filters_update();
    },
    change_day: function(day) {
      this.$inertia.get(
        '',
        {
          day: day,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'day',
            'graph_pnl_histories',
            'graph_wac_histories',
            'graph_balance_histories',
            'assets_table',
            'assets_table_total',
          ],
        },
      );
    },
    chart_update: function() {
      this.$refs.graph_pnl_ref?.chart?.update();
      this.$refs.graph_wac_ref?.chart?.update();
      this.$refs.graph_balance_ref?.chart?.update();
    },
    on_chart_click: function(event, activeElements) {
      if (activeElements.length > 0) {
        this.date_selected = activeElements[0].element.$context.raw[0]
        this.chart_update();
        this.change_day(this.date_selected);
      }
    },
    on_date_picker_click: function(event) {
      this.date_selected = event.replaceAll("/", "-");
      this.change_day(this.date_selected);
      this.chart_update();
    },
    date_annotation: function() {
      return {
        type: "line",
        mode: "vertical",
        scaleID: "x",
        value: Date.parse(this.date_selected),
        borderColor: "#bbb",
        borderWidth: 2,
        borderDash: [8, 12],
        label: {
          display: false,
        },
      };
    },
    currency_format: function(value, symbol) {
      return new Intl.NumberFormat(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          style: "currency",
          currency: symbol,
        }
      ).format(value);
    },
    graph_timerange_change: function(
      timerange, // '1w', '1m', '3m', 'ytd', 'all'
      graph_ref,
      dates_min,
      dates_max,
      date_current,
    ) {
      if (this.graphs_dates_min.length == 0 && this.graphs_dates_max.length == 0) {
        return;
      }

      const graph_min = new Date(dates_min);
      const graph_max = new Date(dates_max);
      const target = date_current != null ? new Date(date_current) : new Date(graph_max);
      let window_min = new Date(graph_min);
      let window_max = new Date(graph_max);

      switch (timerange) {
      case '1w':
        // Sliding Week

        window_min = new Date(new Date(target).setDate(target.getDate() - 3));
        window_max = new Date(new Date(target).setDate(target.getDate() + 3));
        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          window_max = new Date(new Date(graph_min).setDate(graph_min.getDate() + 7));
        }
        if (window_max > graph_max) {
          window_min = new Date(new Date(graph_max).setDate(graph_max.getDate() - 7));
          window_max = new Date(graph_max);
        }
        break;
      case '1m':
        // Discrete Month

        window_min = new Date(new Date(target).setDate(1));
        // Last day of the target month
        window_max = new Date(new Date(target).setDate(1));
        window_max.setMonth(window_max.getMonth() + 1);
        window_max.setDate(0);
        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          // Last day of the month
          window_max = new Date(graph_min);
          window_max.setMonth(window_max.getMonth() + 1);
          window_max.setDate(0);
        }
        if (window_max > graph_max) {
          window_min = new Date(graph_max);
          window_min.setDate(1);
          window_max = new Date(graph_max);
        }
        break;
      case '3m':
        // Discrete 3 Months

        window_min = new Date(new Date(target).setDate(1));
        window_min.setMonth(window_min.getMonth() - 1);
        // Last day of the last month
        window_max = new Date(new Date(target).setDate(1));
        window_max.setMonth(window_max.getMonth() + 2);
        window_max.setDate(0);

        if (window_min < graph_min) {
          window_min = new Date(graph_min);
          // Last day of the month
          window_max = new Date(graph_min);
          window_max.setMonth(window_max.getMonth() + 3);
          window_max.setDate(0);
        }
        if (window_max > graph_max) {
          window_min = new Date(graph_max);
          window_min.setMonth(window_min.getMonth() - 3);
          window_min.setDate(1);
          window_max = new Date(graph_max);
        }
        break;
      case 'ytd':
        // Sliding Year
        window_min = new Date(new Date(target));
        window_min.setYear(target.getFullYear() - 1);
        window_max = new Date(new Date(target));
        break;
      case 'all':
        break;
      }

      if (window_min < graph_min) {
        window_min = graph_min;
      }
      if (window_max > graph_max) {
        window_max = graph_max;
      }

      // Update the X-axis to display the range
      let line = graph_ref;
      if (line && line.chart) {
        const chart = line.chart;
        chart.options.scales.x.min = window_min.getTime();
        chart.options.scales.x.max = window_max.getTime();
        chart.update();
      }
    },
    date_format(date) {
      return qdate.formatDate(date, this.i18n.date_js_format);
    },
  },
};
</script>

<style>
.dashboard-view-selector .q-field__native span {
  color: var(--text-muted) !important;
}
</style>
